/*
  IW Nuevo: collection filter bar (phase 2.3, T21). Loaded with defer by sections/main-collection-product-grid.liquid.
  assets/facets.js still does all the filtering, sorting and markup swapping; <iw-filter-bar> (snippets/iw-facets.liquid)
  only adds what the phone layout needs (below 990px):

  - Sticky offset: the bar sticks under the site header. Dawn's <sticky-header> (sections/header.liquid) slides the
    header back in on scroll-up: the header section has .shopify-section-header-sticky without
    .shopify-section-header-hidden. While it shows, the bar sits at --header-height (class is-below-header), otherwise
    at 0. A MutationObserver follows those classes.
  - Drawer stacking: a sticky element is its own stacking context, so the drawer inside the bar would stay under the
    header. While the drawer is open the bar is lifted (class is-drawer-open).
  - Drawer groups are plain accordions. <menu-drawer> treats every nested <summary> as a sliding submenu (it traps
    focus inside the group and forces aria-expanded="true"). A capture listener stops the click before it reaches
    those handlers; the native <details> toggle still happens, and aria-expanded follows the toggle event.
  - Closing the drawer collapses every group (menu-drawer), so opening it expands the groups with an active filter.
  - While the drawer is open, body gets .iw-filters-open, which hides the Shopify Inbox chat launcher (it sat on top
    of "Ver N productos").
*/
class IwFilterBar extends HTMLElement {
  connectedCallback() {
    // Bind once: a detached and re-attached element runs connectedCallback again.
    if (!this.bound) {
      this.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('.iw-fdrawer .iw-facet__summary')) event.stopPropagation();
        },
        true
      );
      // toggle does not bubble, but a capture listener on an ancestor still receives it.
      this.addEventListener('toggle', (event) => this.onToggle(event), true);
      // Escape inside a drawer group closes the whole drawer. Without this, <menu-drawer> (and global.js's
      // onKeyUpEscape) would treat the group as a submenu and only collapse it.
      this.addEventListener(
        'keyup',
        (event) => {
          if ((event.code || '').toUpperCase() !== 'ESCAPE') return;
          if (!event.target.closest('.iw-fdrawer .iw-facet')) return;
          event.stopPropagation();
          const drawer = this.querySelector('menu-drawer');
          if (drawer && drawer.mainDetailsToggle && drawer.mainDetailsToggle.open) {
            drawer.closeMenuDrawer(event, drawer.mainDetailsToggle.querySelector('summary'));
          }
        },
        true
      );
      this.bound = true;
    }

    this.header = document.querySelector('.section-header');
    if (this.header && 'MutationObserver' in window) {
      this.headerObserver = new MutationObserver(() => this.syncHeader());
      this.headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class'] });
    }
    this.syncHeader();
  }

  disconnectedCallback() {
    if (this.headerObserver) this.headerObserver.disconnect();
  }

  syncHeader() {
    const classes = this.header ? this.header.classList : null;
    const shown =
      !!classes &&
      classes.contains('shopify-section-header-sticky') &&
      !classes.contains('shopify-section-header-hidden');
    this.classList.toggle('is-below-header', shown);
  }

  onToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    if (details.classList.contains('iw-facet')) {
      const summary = details.querySelector('summary');
      if (summary && summary.hasAttribute('aria-expanded')) {
        summary.setAttribute('aria-expanded', String(details.open));
      }
      return;
    }

    if (details.classList.contains('mobile-facets__disclosure')) {
      this.classList.toggle('is-drawer-open', details.open);
      document.body.classList.toggle('iw-filters-open', details.open);
      if (details.open) {
        // Read the state from the inputs: facets.js refreshes each group's content, not its attributes.
        this.querySelectorAll('.iw-fdrawer .iw-facet').forEach((group) => {
          const priceSet = Array.from(group.querySelectorAll('.iw-price__input')).some((input) => input.value);
          if (priceSet || group.querySelector('input:checked')) group.open = true;
        });
      }
    }
  }
}

if (!customElements.get('iw-filter-bar')) customElements.define('iw-filter-bar', IwFilterBar);

/*
  Price inputs (snippets/iw-facet-groups.liquid) take whole pesos only. In Chile "100.000" means one hundred thousand,
  but facets.js would send it as is and the filter would read 100. Every non-digit is stripped on input, in a document
  capture listener so it runs before facets.js reads the form (sidebar and drawer alike).
*/
if (!window.iwPriceInputSanitizer) {
  window.iwPriceInputSanitizer = true;
  document.addEventListener(
    'input',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.classList.contains('iw-price__input')) return;
      const digits = input.value.replace(/\D/g, '');
      if (digits !== input.value) input.value = digits;
    },
    true
  );
}
